<template>
  <div class="min-h-screen bg-white">
    <!-- Sidebar -->
    <div class="fixed inset-y-0 left-0 z-50 w-72 bg-gradient-to-b from-slate-900 to-slate-800 shadow-2xl transform transition-all duration-300 ease-in-out lg:translate-x-0 flex flex-col" :class="{ '-translate-x-full': !sidebarOpen, 'translate-x-0': sidebarOpen }">
      <!-- Logo -->
      <div class="flex items-center justify-center h-25 p-6 border-b border-slate-700 shrink-0">
        <div class="flex items-center">
          <div class="h-20 w-20 bg-gradient-to-r  to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
            <img src="/images/logo.png" alt="Logo" class="h-20 w-20" />
          </div>
          <div class="ml-3">
            <span class="text-xl font-bold text-white">Mascotijo</span>
            <p class="text-xs text-slate-400">Admin Dashboard</p>
          </div>
        </div>
      </div>

      <!-- Navigation -->
      <nav class="mt-8 px-4 flex-1 overflow-y-auto min-h-0">
        <div class="space-y-1">
          <router-link
            v-for="item in navigation"
            :key="item.name"
            :to="item.to"
            class="sidebar-link group"
            :class="$route.name === item.name ? 'sidebar-link-active' : 'sidebar-link-inactive'"
            v-show="!item.adminOnly || authStore.isAdmin"
          >
            <div class="flex items-center">
              <component :is="item.icon" class="mr-3 h-5 w-5 transition-colors" />
              <span class="font-medium">{{ item.label }}</span>
            </div>
            <div v-if="$route.name === item.name" class="ml-auto w-1 h-8 bg-blue-500 rounded-full"></div>
          </router-link>
        </div>
      </nav>

      <!-- User info -->
      <div class="shrink-0 p-4 border-t border-slate-700 relative" ref="sidebarUserRef">
        <div
          class="flex items-center p-3 rounded-lg bg-slate-800/50 hover:bg-slate-700/50 transition-colors cursor-pointer"
          @click="sidebarUserOpen = !sidebarUserOpen"
        >
          <div class="h-10 w-10 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold">
            {{ authStore.user?.name?.charAt(0).toUpperCase() }}
          </div>
          <div class="ml-3 flex-1">
            <p class="text-sm font-medium text-white">{{ authStore.user?.name }}</p>
            <p class="text-xs text-slate-400 capitalize flex items-center">
              <span class="w-2 h-2 bg-green-400 rounded-full mr-2"></span>
              {{ authStore.user?.role }}
            </p>
          </div>
          <svg class="h-4 w-4 text-slate-400 transition-transform" :class="{ 'rotate-90': sidebarUserOpen }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </div>

        <!-- Sidebar user dropdown -->
        <div class="hidden sm:block">
          <div v-if="sidebarUserOpen" class="absolute left-full bottom-0 ml-3 w-56 bg-white rounded-xl shadow-xl border border-gray-200 py-2 z-50">
          <div class="px-4 py-3 border-b border-gray-100">
            <p class="text-sm font-semibold text-gray-900">{{ authStore.user?.name }}</p>
            <p class="text-xs text-gray-500 mt-0.5">{{ authStore.user?.email }}</p>
            <span class="inline-flex items-center mt-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 capitalize">{{ authStore.user?.role }}</span>
          </div>
          <button @click="logout" class="flex items-center w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors">
            <svg class="mr-3 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Cerrar Sesión
          </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Mobile sidebar overlay -->
    <div v-if="sidebarOpen" class="fixed inset-0 z-40 lg:hidden" @click="sidebarOpen = false">
      <div class="absolute inset-0 bg-black/50 backdrop-blur-sm"></div>
    </div>

    <!-- Main content -->
    <div class="lg:pl-72">
      <!-- Top bar -->
      <div class="sticky top-0 z-30 bg-white/80 backdrop-blur-md shadow-sm border-b border-gray-200/50">
        <div class="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
          <div class="flex items-center">
            <button
              @click="sidebarOpen = !sidebarOpen"
              class="lg:hidden p-2 rounded-xl text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
            >
              <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <div class="ml-4 hidden sm:block">
              <h1 class="text-2xl font-bold text-gray-900">{{ pageTitle }}</h1>
              <p class="text-sm text-gray-500">{{ getPageDescription() }}</p>
            </div>
          </div>

          <div class="flex items-center space-x-4">
            <!-- Search -->
          

            <!-- Notifications -->
            <NotificationBell />

            <!-- User menu -->
            <div class="relative" ref="userMenuRef">
              <button
                @click="userMenuOpen = !userMenuOpen"
                class="flex items-center p-2 text-sm rounded-xl hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
              >
                <div class="h-8 w-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold text-sm">
                  {{ authStore.user?.name?.charAt(0).toUpperCase() }}
                </div>
                <svg class="ml-2 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              <!-- User dropdown -->
              <div v-if="userMenuOpen" class="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50">
                <div class="px-4 py-3 border-b border-gray-100">
                  <p class="text-sm font-medium text-gray-900">{{ authStore.user?.name }}</p>
                  <p class="text-sm text-gray-500">{{ authStore.user?.email }}</p>
                </div>

                <button @click="logout" class="flex items-center w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors">
                  <svg class="mr-3 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  Cerrar Sesión
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Page content -->
      <main class="p-4 sm:p-6 lg:p-8">
        <router-view />
      </main>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import NotificationBell from '../components/NotificationBell.vue'

// Icons
import {
  HomeIcon,
  UsersIcon,
  UserIcon,
  CogIcon,
  HeartIcon,
  TagIcon,
  RectangleGroupIcon,
  PlusCircleIcon,
  BookOpenIcon,
  BeakerIcon,
  DocumentTextIcon,
  NoSymbolIcon,
  FlagIcon,
  MegaphoneIcon,
  CurrencyDollarIcon
} from '@heroicons/vue/24/outline'

export default {
  name: 'DashboardLayout',
  components: {
    NotificationBell
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const authStore = useAuthStore()
    
    const sidebarOpen = ref(false)
    const userMenuOpen = ref(false)
    const userMenuRef = ref(null)
    const sidebarUserOpen = ref(false)
    const sidebarUserRef = ref(null)

    watch(() => route.path, () => {
      sidebarOpen.value = false
      window.scrollTo({ top: 0, behavior: 'instant' })
    })

    const navigation = [
      {
        name: 'Dashboard',
        label: 'Dashboard',
        to: { name: 'Dashboard' },
        icon: HomeIcon,
        adminOnly: false,
        description: 'Vista general del sistema'
      },
      {
        name: 'Users',
        label: 'Usuarios',
        to: { name: 'Users' },
        icon: UsersIcon,
        adminOnly: true,
        description: 'Gestión de usuarios del sistema'
      },
      {
        name: 'Pets',
        label: 'Mascotas',
        to: { name: 'Pets' },
        icon: HeartIcon,
        adminOnly: false,
        description: 'Registro y gestión de mascotas'
      },
      {
        name: 'AnimalTypes',
        label: 'Tipos de Animales',
        to: { name: 'AnimalTypes' },
        icon: RectangleGroupIcon,
        adminOnly: true,
        description: 'Configuración de tipos de animales'
      },
      {
        name: 'Breeds',
        label: 'Razas',
        to: { name: 'Breeds' },
        icon: TagIcon,
        adminOnly: true,
        description: 'Gestión de razas por tipo de animal'
      },
      {
        name: 'Health',
        label: 'Salud',
        to: { name: 'Health' },
        icon: PlusCircleIcon,
        adminOnly: true,
        description: 'Artículos y consejos de salud para mascotas'
      },
      {
        name: 'Education',
        label: 'Educación',
        to: { name: 'Education' },
        icon: BookOpenIcon,
        adminOnly: true,
        description: 'Contenido educativo sobre cuidado de mascotas'
      },
      {
        name: 'Nutrition',
        label: 'Nutrición',
        to: { name: 'Nutrition' },
        icon: BeakerIcon,
        adminOnly: true,
        description: 'Guías de alimentación y nutrición'
      },
      {
        name: 'Legal',
        label: 'Documentos Legales',
        to: { name: 'Legal' },
        icon: DocumentTextIcon,
        adminOnly: true,
        description: 'Políticas de privacidad y términos y condiciones'
      },
      {
        name: 'Blocks',
        label: 'Bloqueos',
        to: { name: 'Blocks' },
        icon: NoSymbolIcon,
        adminOnly: true,
        description: 'Gestión de bloqueos entre usuarios'
      },
      {
        name: 'Reports',
        label: 'Reportes',
        to: { name: 'Reports' },
        icon: FlagIcon,
        adminOnly: true,
        description: 'Reportes de usuarios y mensajes'
      },
      {
        name: 'ScheduledMessages',
        label: 'Mensajes Programados',
        to: { name: 'ScheduledMessages' },
        icon: MegaphoneIcon,
        adminOnly: true,
        description: 'Envío de notificaciones push programadas'
      },
      {
        name: 'Subscriptions',
        label: 'Suscripciones',
        to: { name: 'Subscriptions' },
        icon: CurrencyDollarIcon,
        adminOnly: true,
        description: 'Gestión de planes Free y Premium'
      }
    ]

    const pageTitle = computed(() => {
      const currentRoute = navigation.find(item => item.name === route.name)
      return currentRoute ? currentRoute.label : 'Panel de Administración'
    })

    const getPageDescription = () => {
      const currentRoute = navigation.find(item => item.name === route.name)
      return currentRoute ? currentRoute.description : 'Sistema de gestión de mascotas'
    }

    const logout = async () => {
      try {
        await authStore.logout()
        router.push('/login')
      } catch (error) {
        console.error('Error during logout:', error)
        router.push('/login')
      }
    }

    const handleClickOutside = (event) => {
      if (userMenuRef.value && !userMenuRef.value.contains(event.target)) {
        userMenuOpen.value = false
      }
      if (sidebarUserRef.value && !sidebarUserRef.value.contains(event.target)) {
        sidebarUserOpen.value = false
      }
    }

    onMounted(() => {
      document.addEventListener('click', handleClickOutside)
    })

    onUnmounted(() => {
      document.removeEventListener('click', handleClickOutside)
    })

    return {
      authStore,
      sidebarOpen,
      userMenuOpen,
      userMenuRef,
      sidebarUserOpen,
      sidebarUserRef,
      navigation,
      pageTitle,
      getPageDescription,
      logout
    }
  }
}
</script>
